import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { RadioButton } from "primereact/radiobutton";

import { InputText } from "primereact/inputtext";
import {
  ComplaintAssessmentsDTO,
  ComplaintStatus,
  statusList,
  StepCallBack,
} from "@modules/plaintes/plaintes.model.ts";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { useAppDispatch, useAppSelector } from "@core/store/hook.ts";
import { dataUser } from "@core/slice/UsersSlice.ts";
import {
  recordValid,
  selectedStatusStepFive,
  setSelectedStatus,
  setStepReasonValue,
  setTextAreaValue,
  stepReasonValue,
  textAreaStepFive,
} from "@core/slice/ComplaintSlice.ts";
import Message from "@shared/message/Message.tsx";
import { getProfilLookup } from "@core/actions/usersActions.ts";
import ReadingAloud from "@modules/plaintes/complaintManaging/addingComplaint/dialog/ReadingAloud.tsx";
import { classNames } from "primereact/utils";

const MeasureToBeTaken: React.FC<StepCallBack> = ({
  onPrevious,
  onResolve,
}) => {
  const dispatch = useAppDispatch();
  const stepReason = useAppSelector(stepReasonValue);
  const userInfos = useAppSelector(dataUser);
  const textAreaValue = useAppSelector(textAreaStepFive);
  const selectedStatus = useAppSelector(selectedStatusStepFive);
  const isRecordValid = useAppSelector(recordValid);
  const [resolved, setResolved] = useState<boolean | null>(true);
  const [isReadingAloud, setIsReadingAloud] = useState<boolean>(false);
  const [complaintDetails, setComplaintDetails] = useState({
    natureOfComplaint: "",
    complainantName: "",
    complaintdoneBy: "",
  });
  const [complaintAssessments, setComplaintAssessments] =
    useState<ComplaintAssessmentsDTO>({
      id: 0,
      complaintId: 0,
      rsuRelated: true,
      sensivity: "",
      resolutionLevel: "LOCAL",
    });
  const [open, setOpen] = useState(false);
  const [messageInfo, setMessageInfo] = useState<{
    severity: "success" | "info" | "warn" | "error";
    summary: string;
    detail: string;
  }>({
    severity: "success",
    summary: "",
    detail: "",
  });

  const [errors, setErrors] = useState({
    status: "",
    action: "",
  });
  React.useEffect(() => {
    dispatch(getProfilLookup());
  }, [dispatch]);

  useEffect(() => {
    if (stepReason && stepReason.stepFour) {
      const joinedNatureComplaintNames =
        stepReason.stepFour.natureComplaintSelectedList
          .map((item) => item.name)
          .join(", ");
      setComplaintDetails((prevDetails) => ({
        ...prevDetails,
        natureOfComplaint: joinedNatureComplaintNames,
      }));
      if (stepReason.stepTwo) {
        setComplaintDetails((prevDetails) => ({
          ...prevDetails,
          complainantName: stepReason.stepTwo?.anonym
            ? "Plaignant anonyme"
            : stepReason.stepTwo?.fullName ?? "",
        }));
      }
    }

    if (userInfos) {
      try {
        const plainteDoneByName = `${userInfos.lastName} ${userInfos.firstName}`;
        setComplaintDetails((prevDetails) => ({
          ...prevDetails,
          complaintdoneBy: plainteDoneByName,
        }));
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
      }
    }
  }, [userInfos, stepReason]);

  useEffect(() => {
    if (onResolve && resolved !== null && resolved === false) {
      onResolve(resolved);
    }
    if (resolved) {
      setComplaintAssessments((prevAssesment) => ({
        ...prevAssesment,
        resolutionLevel: "LOCAL",
      }));
    } else {
      setComplaintAssessments((prevAssesment) => ({
        ...prevAssesment,
        resolutionLevel: "REGIONAL",
      }));
    }
  }, [onResolve, resolved, dispatch, stepReason]);

  React.useEffect(() => {
    if (stepReason) {
      dispatch(
        setStepReasonValue({ ...stepReason, stepFive: complaintAssessments })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, complaintAssessments]);

  function handlePreviousStep() {
    if (onPrevious) {
      onPrevious();
    }
  }

  const handleAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch(setTextAreaValue(e.target.value));

    setErrors((error) => ({
      ...error,
      action: "",
    }));
  };
  const isCompleteInfo =
    Object.keys(selectedStatus).length !== 0 &&
    textAreaValue.trim() !== "" &&
    isRecordValid === false;

  return (
    <div className="column gap-[20px] h-12rem card-stepper">
      <Message
        open={open}
        severity={messageInfo.severity}
        summary={messageInfo.summary}
        detail={messageInfo.detail}
      />
      <ReadingAloud
        isReadingAloud={isReadingAloud}
        setIsReadingAloud={setIsReadingAloud}
        setMessageInfo={setMessageInfo}
        setOpen={setOpen}
      />
      <div className="stitle">Les solutions à mettre en œuvre</div>
      <div className="mb-6">
        <p className="font-medium mb-2">
          La plainte a-t-elle été résolue et la demande d'information a-t-elle
          reçu une réponse ?
        </p>
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <RadioButton
              inputId="resolvedYes"
              name="resolvedYes"
              value={resolved}
              checked={resolved === true}
              onChange={() => setResolved(true)}
            />
            <label htmlFor="resolvedYes" className="ml-2">
              Oui
            </label>
          </div>
          <div className="flex items-center">
            <RadioButton
              inputId="resolvedNo"
              name="resolvedNo"
              value={resolved}
              checked={resolved === false}
              onChange={() => setResolved(false)}
            />
            <label htmlFor="resolvedNo" className="ml-2">
              Non
            </label>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="complaint" className="block font-medium mb-2">
            Plainte relative à
          </label>
          <InputText
            id="complaint"
            name="complaint"
            value={complaintDetails.natureOfComplaint || ""}
            className="w-full"
            placeholder="Violence physique"
            disabled
          />
        </div>
        <div>
          <label htmlFor="doneBy" className="block font-medium mb-2">
            Fait par
          </label>
          <InputText
            id="doneBy"
            name="doneBy"
            value={complaintDetails.complaintdoneBy || ""}
            className="w-full"
            placeholder="Rakoto Lita"
            disabled
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="complaint" className="block font-medium mb-2">
            Nom du plaignant
          </label>
          <InputText
            id="complaint"
            name="complaint"
            value={complaintDetails.complainantName || ""}
            className="w-full"
            placeholder="RAZAKASOA"
            disabled
          />
        </div>
        <div>
          <label htmlFor="status">Changer le statut (obligatoire)</label>

          <Dropdown
            id="status"
            value={selectedStatus}
            onChange={(e: DropdownChangeEvent) => {
              dispatch(setSelectedStatus(e.value));
              setErrors((error) => ({
                ...error,
                status: "",
              }));
            }}
            options={statusList.filter(
              (status) =>
                status.code === ComplaintStatus.RESOLVED_POSITIVE ||
                status.code === ComplaintStatus.ESCALATED||
                status.code === ComplaintStatus.SAVED ||
                status.code === ComplaintStatus.IN_PROGRESS_VALIDATION ||
                status.code === ComplaintStatus.CURRENTLY_PROCESSED ||
                status.code === ComplaintStatus.AWAITING_RESPONSE ||
                status.code === ComplaintStatus.RESOLVED_NEGATIVE 
                 // status.code === ComplaintStatus.ARCHIVED ||
                // status.code === ComplaintStatus.IN_PROGRESS ||
                // status.code === ComplaintStatus.RESOLVED
            )}
            optionLabel="name"
            placeholder="---Changer le statut---"
            className="w-full md:w-14rem"
          />
          {errors.status && (
            <small className="text-red-600">{errors.status}</small>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="complainantName" className="block font-medium mb-2">
          Action entreprise (obligatoire)
        </label>
        <InputTextarea
          value={textAreaValue}
          onChange={handleAreaChange}
          rows={5}
          placeholder="Entrez votre texte ici..."
          autoResize
          className="w-full"
        />
        {errors.action && (
          <small className="text-red-600">{errors.action}</small>
        )}
      </div>
      <div className="w-full flex justify-end gap-3 items-baseline">
        <Button
          label="Précédent"
          className="btn btn-secondary w-[120px]"
          onClick={handlePreviousStep}
        />
        <Button
          label="Lire à haute voix"
          className={classNames("btn btn-primary w-[150px]", {
            "btn-primary-disabled": !isCompleteInfo,
          })}
          disabled={!isCompleteInfo}
          onClick={() => setIsReadingAloud(true)}
        />
      </div>
    </div>
  );
};

export default MeasureToBeTaken;
